import {
  AssetType,
  Agent,
  ZapMetadata,
  ZapierAgentWorkflow,
  isZapErrorObject,
} from "@zapier/canvas-state";
import {
  ON_DEMAND_WORKFLOW_ACTION_KEY,
  TRIGGER_VIA_ZAP_ACTION_KEY,
} from "@zapier/canvas-constants";

export interface AssetStatus {
  isEnabled: boolean;
  isDraft: boolean;
}

export function getAssetStatus(
  asset: unknown,
  assetType: AssetType,
): AssetStatus | null {
  const isZapNotFound = isZapErrorObject(asset as ZapMetadata);
  if (!asset || isZapNotFound) {
    return null;
  }

  switch (assetType) {
    case "zap": {
      const zapAsset = asset as ZapMetadata;
      return {
        isEnabled: zapAsset?.is_enabled ?? false,
        isDraft: !zapAsset?.is_published,
      };
    }
    case "agent": {
      const agentAsset = asset as Agent;
      return {
        isEnabled: agentAsset?.workflow?.enabled ?? false,
        isDraft: false, // Agents are always considered published
      };
    }
    default:
      return null;
  }
}

export function getDraftTooltipContent(assetType: AssetType): string {
  switch (assetType) {
    case "zap":
      return "Zap is incomplete and cannot be turned on";
    case "agent":
      return "This agent cannot be enabled. Check the agent settings to make sure it is properly configured";
    default:
      return "This asset cannot be enabled";
  }
}

export function getStatusTooltipContent(
  assetType: AssetType,
  isEnabled: boolean,
): string {
  const statusText = isEnabled ? "on" : "off";

  switch (assetType) {
    case "zap":
      return `Zap is ${statusText}`;
    case "agent":
      return `Agent is ${statusText}`;
    default:
      return `Asset is ${statusText}`;
  }
}

// Agent-specific utilities

/**
 * Determines if an agent is always enabled based on its trigger type
 * On-demand and zap-triggered agents are always considered "enabled"
 */
export function isAgentAlwaysEnabled(workflow: ZapierAgentWorkflow): boolean {
  const actionKey = workflow.trigger?.action_key;
  return (
    actionKey === ON_DEMAND_WORKFLOW_ACTION_KEY ||
    actionKey === TRIGGER_VIA_ZAP_ACTION_KEY
  );
}

/**
 * Checks if agent has proper configuration (instructions and trigger)
 */
export function hasAgentConfiguration(workflow: ZapierAgentWorkflow): boolean {
  const hasInstructions = Boolean(
    workflow.instructions || workflow.instructions_preview_text,
  );
  const hasTrigger = Boolean(workflow.trigger);

  return hasInstructions && hasTrigger;
}

/**
 * Gets the appropriate tooltip content for an agent toggle
 * @param workflow - The agent's workflow
 * @param isLoading - Whether the toggle is currently loading
 * @returns Tooltip content string or undefined if no tooltip should be shown
 */
export function getAgentTooltipContent(
  workflow: ZapierAgentWorkflow,
  isLoading: boolean = false,
): string | undefined {
  const actionKey = workflow.trigger?.action_key;

  // Special trigger types that are always enabled
  if (actionKey === ON_DEMAND_WORKFLOW_ACTION_KEY) {
    return "On-demand agents are always enabled";
  }

  if (actionKey === TRIGGER_VIA_ZAP_ACTION_KEY) {
    return "This agent is triggered when the Zap runs";
  }

  // Check if agent is disabled and missing configuration
  if (!workflow.enabled && !hasAgentConfiguration(workflow)) {
    return "This agent cannot be enabled. Check the agent settings to make sure it is properly configured.";
  }

  // If enabled or being toggled, show status
  if (workflow.enabled) {
    return "Agent is on";
  }

  if (isLoading) {
    return undefined;
  }

  // Agent is off and properly configured
  return "Agent is off";
}
